import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Mode } from "../../types";
import { useInfiniteGroupListQuery } from "../queries/useInfiniteGroupListQuery";
import {
  useUpdateGroupMutation,
  useDeleteGroupMutation,
} from "../queries/mutations/useGroupMutations";
import { useAddToStorageMutation } from "../queries/mutations/useStorageMutations";
import { useGroupListStore } from "../../store";

/**
 * useGroupMenuListView
 *
 * 그룹 목록(튜터 목록/소크라테스 목록) 전용 presentation 훅
 *
 * 기능:
 * - 그룹 목록 무한 스크롤 데이터 페칭
 * - 날짜별 섹션 그룹핑
 * - 아이템 클릭/편집/삭제/보관 핸들러
 */

interface UseGroupMenuListViewOptions {
  mode: Mode;
}

interface GroupedItem {
  id: string;
  label: string;
  date?: string;
  showDate?: boolean;
  isStorage: false;
  isCompleted?: boolean;
}

export const useGroupMenuListView = (options: UseGroupMenuListViewOptions) => {
  const { mode } = options;

  const navigate = useNavigate();
  const { setCurrentGroupId } = useGroupListStore();

  // Infinite Query
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteGroupListQuery({ mode });

  // Mutations
  const updateGroupMutation = useUpdateGroupMutation(mode);
  const deleteGroupMutation = useDeleteGroupMutation(mode);
  const addToStorageMutation = useAddToStorageMutation(mode);

  // 편집 상태
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  /**
   * 날짜별 그룹핑 및 items 변환
   */
  const groupedItems = useMemo(() => {
    if (!data) return [];

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const allGroups = data.pages.flatMap((page) => page.groups);
    const grouped: Record<string, GroupedItem[]> = {};

    allGroups.forEach((item) => {
      const itemDate = new Date(item.updated_at);
      const itemDateOnly = new Date(
        itemDate.getFullYear(),
        itemDate.getMonth(),
        itemDate.getDate()
      );

      let section: string;

      if (itemDateOnly.getTime() === today.getTime()) {
        section = "오늘";
      } else if (itemDateOnly >= sevenDaysAgo) {
        section = "지난 7일";
      } else {
        const year = itemDate.getFullYear();
        const month = itemDate.getMonth() + 1;
        section = `${year}년 ${month}월`;
      }

      if (!grouped[section]) {
        grouped[section] = [];
      }

      grouped[section].push({
        id: item.id.toString(),
        label: item.title || "제목 없음",
        date: section,
        showDate: false, // 나중에 설정
        isStorage: false,
        isCompleted: item.is_completed,
      });
    });

    // 각 섹션의 첫 항목에만 날짜 표시
    const result: GroupedItem[] = [];
    Object.entries(grouped).forEach(([_, items]) => {
      items.forEach((item, index) => {
        result.push({
          ...item,
          showDate: index === 0,
        });
      });
    });

    return result;
  }, [data]);

  /**
   * 아이템 클릭 - 해당 그룹으로 네비게이션
   */
  const handleItemClick = useCallback(
    (itemId: string) => {
      const navState = {
        from_chat: "group" as const,
        his_cnt: 0,
        is_writed: false,
        is_completed: false,
      };

      // mode에 따라 라우팅 경로 결정 (현재 pageName이 아닌 mode 기준)
      const basePath = mode === Mode.TUTOR ? "tutor" : "socrates";

      setCurrentGroupId(itemId);
      navigate(`/${basePath}/${itemId}`, { state: navState });
    },
    [navigate, mode, setCurrentGroupId]
  );

  /**
   * 편집 모드 진입
   */
  const handleItemEdit = useCallback((itemId: string) => {
    setEditingItemId(itemId);
  }, []);

  /**
   * 그룹 삭제
   */
  const handleItemDelete = useCallback(
    async (itemId: string) => {
      await deleteGroupMutation.mutateAsync(itemId);
    },
    [deleteGroupMutation]
  );

  /**
   * 보관함에 저장
   */
  const handleItemSave = useCallback(
    async (itemId: string) => {
      const item = groupedItems.find((i) => i.id === itemId);
      if (!item) return;

      const result = await addToStorageMutation.mutateAsync({
        groupId: itemId,
        title: item.label,
        isCompleted: item.isCompleted,
      });

      if (!result.success && result.message) {
        console.warn(result.message);
      }
    },
    [addToStorageMutation, groupedItems]
  );

  /**
   * 제목 변경 완료
   */
  const handleItemTitleChange = useCallback(
    async (itemId: string, newTitle: string) => {
      await updateGroupMutation.mutateAsync({
        groupId: itemId,
        title: newTitle,
      });

      setEditingItemId(null);
    },
    [updateGroupMutation]
  );

  /**
   * 무한 스크롤 핸들러
   */
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    // 데이터
    items: groupedItems,
    label: mode === Mode.TUTOR ? "튜터 목록" : "소크라테스 목록",

    // 상태
    editingItemId,
    isLoading:
      isLoading ||
      updateGroupMutation.isPending ||
      deleteGroupMutation.isPending ||
      addToStorageMutation.isPending,
    hasMore: hasNextPage,
    isFetchingMore: isFetchingNextPage,

    // 핸들러
    handleItemClick,
    handleItemEdit,
    handleItemDelete,
    handleItemSave,
    handleItemTitleChange,
    handleLoadMore,
  };
};
